import React, { useState, useEffect } from 'react';
import { PenTool, Calendar, Search, Plus, X, Eye, AlertTriangle } from 'lucide-react';
import { api } from '../lib/api';
import useBloqueioScroll from '../hooks/useBloqueioScroll';

// Corrigido (26/08/2026, bug real reportado pelo usuário: "Invalid Date" na
// coluna Data) — dataStr vem AAAAMMDD (Caractere, ZN1_DTAGEN), sem separador;
// new Date("20260826") não é um formato que o construtor Date reconhece
// (só entende "2026-08-26"). Monta a data direto da string, sem passar por
// Date nenhuma — mesmo cuidado de sempre no resto do sistema.
const formatarData = (dataStr) =>
    (dataStr && dataStr.length === 8) ? `${dataStr.slice(6, 8)}/${dataStr.slice(4, 6)}/${dataStr.slice(0, 4)}` : '—';

// Corrigido (26/08/2026, bug real reportado pelo usuário: "String or binary
// data would be truncated... Truncated value: 'NaNNaNNa'") — diferente de
// ChaoFabrica.jsx/agendamento.js (que espera AAAAMMDD pronto, sem traço), o
// backend de manutenção (manutencao.js) usa aaaammddDeEntrada() pras datas
// (mesma função usada em setup.js) — essa função espera receber o valor CRU
// do <input type="date"> ("AAAA-MM-DD", com traço) e ela mesma converte pra
// AAAAMMDD do lado do servidor. Mandar já convertido (sem traço, como eu
// tinha feito) faz a regex dela não bater, cai no fallback `new Date(valor)`
// — que devolve Invalid Date pra uma string sem traço, virando "NaN" nos 3
// pedaços. Por isso aqui o estado do formulário guarda a data em ISO
// ("AAAA-MM-DD") direto, sem nenhuma conversão — é literalmente o que
// <input type="date"> já produz sozinho.
const hojeISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
// Só usado pra PRÉ-POPULAR o formulário a partir de uma OS já existente — o
// backend sempre devolve a data em AAAAMMDD (Caractere), nunca em ISO.
const aaaammddParaISO = (s) => (s && s.length === 8 ? `${s.slice(0, 4)}-${s.slice(4, 6)}-${s.slice(6, 8)}` : '');

// Formulário real da Meditec (om.pdf, "Solicitação de Manutenção") só usa
// esses 2 tipos — sem opção de texto livre, pra manter ZN1_TIPO consistente
// (usado hoje só como filtro/rótulo na listagem).
const TIPOS_OS = ['Corretiva', 'Preventiva'];

const Manutencao = () => {
    const [manutencoes, setManutencoes] = useState([]);
    const [carregando, setCarregando] = useState(true);
    const [erro, setErro] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        api.get('/api/manutencao')
            .then(setManutencoes)
            .catch((err) => setErro(err.message))
            .finally(() => setCarregando(false));
    }, []);

    // Modal "Nova OS" (25/08/2026, pedido do usuário) — digitalização da
    // ABERTURA do formulário real de Solicitação de Manutenção da Meditec
    // (om.pdf): só os campos preenchidos na hora do pedido (Solicitante,
    // Equipamento, Data, Hora de Parada, Descrição do Serviço). A seção "Para
    // uso do executante" do papel (Defeito/Causa/Ação/Liberação/Responsáveis)
    // é preenchida DEPOIS, no fechamento da OS — fluxo à parte, ainda não
    // pedido (ver database/especificacao_zn1_manutencao_os.txt).
    const [maquinas, setMaquinas] = useState([]);
    useEffect(() => {
        api.get('/api/maquinas').then(setMaquinas).catch(() => { /* select cai pra "Carregando..." se falhar */ });
    }, []);
    const [novaOS, setNovaOS] = useState(null);
    const [formErro, setFormErro] = useState('');
    const [salvando, setSalvando] = useState(false);

    const handleAbrirNovaOS = () => {
        setFormErro('');
        setNovaOS({
            maquina_id: '', tipo: 'Corretiva', solicitante: '',
            data_agendada: hojeISO(), hora_parada: '', descricao: '',
            data_prevista: '', hora_prevista: '',
        });
    };

    const handleCriarOS = async () => {
        if (!novaOS.maquina_id) { setFormErro('Selecione o equipamento.'); return; }
        if (!novaOS.solicitante.trim()) { setFormErro('Informe o solicitante.'); return; }
        if (!novaOS.descricao.trim()) { setFormErro('Descreva o serviço solicitado.'); return; }
        // Obrigatório (26/08/2026, pedido do usuário) — sem uma data prevista
        // de término, o Gantt não tem como desenhar a janela futura da
        // manutenção sem inventar uma duração (ver comentário em
        // obterChaoFabrica, producao.js: virou "não aparece" em vez de
        // chutar um padrão).
        if (!novaOS.data_prevista) { setFormErro('Informe a previsão de término.'); return; }
        setSalvando(true);
        setFormErro('');
        try {
            const criada = await api.post('/api/manutencao', novaOS);
            // O INSERT no backend devolve só as colunas da própria ZN1 (não
            // faz o JOIN com a ZM1/SH1 que a listagem usa pra resolver nome
            // do equipamento) — sem isso, a linha recém-criada aparecia com
            // "—" no Equipamento até a página ser recarregada (bug real
            // reportado pelo usuário: "não está puxando a maquina"). Já
            // temos essa mesma informação carregada no `maquinas` (select do
            // formulário), então completa aqui na hora, sem round-trip extra.
            const maquina = maquinas.find((m) => m.id === novaOS.maquina_id);
            const criadaComMaquina = maquina
                ? { ...criada, maquina_codigo: maquina.codigo_protheus, maquina_nome: maquina.nome }
                : criada;
            setManutencoes((lista) => [criadaComMaquina, ...lista]);
            setNovaOS(null);
        } catch (err) {
            setFormErro(err.message || 'Erro ao criar a OS.');
        } finally {
            setSalvando(false);
        }
    };

    // Confirmação de exclusão (26/08/2026, pedido do usuário: "traga em
    // formato de modal esse tipo de questionamento") — window.confirm() é o
    // diálogo NATIVO do navegador (aparece com a URL do sistema, "192.168.x.x
    // diz", fora do visual do resto da tela), trocado por um modal próprio,
    // no mesmo padrão dos outros modais desta tela.
    const [excluindo, setExcluindo] = useState(null);
    const [confirmarExcluir, setConfirmarExcluir] = useState(null);
    const handleExcluirOS = async () => {
        const os = confirmarExcluir;
        if (!os) return;
        setConfirmarExcluir(null);
        setExcluindo(os.id);
        try {
            await api.delete(`/api/manutencao/${os.id}`);
            setManutencoes((lista) => lista.filter((m) => m.id !== os.id));
        } catch (err) {
            setErro(err.message || 'Erro ao excluir a OS.');
        } finally {
            setExcluindo(null);
        }
    };

    // Modal "Concluir OS" (25/08/2026, pedido do usuário) — o resto do
    // formulário real, seção "PARA USO DO EXECUTANTE": Recebedor, Defeito,
    // Causa, Ação, Liberação pra produção, Responsáveis. Ação separada da
    // criação, por decisão do usuário — a manutenção acontece DEPOIS do
    // pedido, não no mesmo momento.
    const [concluirOS, setConcluirOS] = useState(null);
    const [concluirErro, setConcluirErro] = useState('');
    const [concluindo, setConcluindo] = useState(false);

    // Modal "Detalhes da OS" (26/08/2026, pedido do usuário: "poder clicar
    // na manutenção e ver o resumo geral, todas as informações, desde o
    // modal de abertura e o de finalização") — somente-leitura, junta os
    // campos dos dois formulários (Nova OS + Concluir OS) numa tela só, pra
    // não precisar abrir "Concluir" (que é editável, pensado pra preencher,
    // não só consultar) só pra ver o que já foi registrado.
    const [detalheOS, setDetalheOS] = useState(null);
    useBloqueioScroll(!!novaOS || !!concluirOS || !!detalheOS || !!confirmarExcluir);

    const handleAbrirConcluirOS = (os) => {
        setConcluirErro('');
        setConcluirOS({
            id: os.id,
            recebedor: os.recebedor || '', data_realizada: os.data_realizada ? aaaammddParaISO(os.data_realizada) : hojeISO(), hora_recebedor: os.hora_recebedor || '',
            defeito: os.defeito || '', causa: os.causa || '', acao: os.acao || '',
            liberada: os.liberada ?? true,
            responsavel_analise: os.responsavel_analise || '', responsavel_arquivamento: os.responsavel_arquivamento || '',
            data_arquivamento: os.data_arquivamento ? aaaammddParaISO(os.data_arquivamento) : hojeISO(),
        });
    };

    const handleConcluirOS = async () => {
        if (!concluirOS.recebedor.trim()) { setConcluirErro('Informe quem recebeu o serviço executado.'); return; }
        if (!concluirOS.defeito.trim() || !concluirOS.causa.trim() || !concluirOS.acao.trim()) { setConcluirErro('Preencha Defeito, Causa e Ação.'); return; }
        setConcluindo(true);
        setConcluirErro('');
        try {
            const { id, ...dados } = concluirOS;
            const atualizada = await api.put(`/api/manutencao/${id}`, {
                ...dados,
                status: concluirOS.liberada ? 'Concluída' : 'Em Andamento',
            });
            setManutencoes((lista) => lista.map((m) => (m.id === id ? atualizada : m)));
            setConcluirOS(null);
        } catch (err) {
            setConcluirErro(err.message || 'Erro ao concluir a OS.');
        } finally {
            setConcluindo(false);
        }
    };

    const getStatusColor = (status) => {
        if (status === 'Concluída') return 'text-petrol-400 bg-petrol-500/10';
        if (status === 'Em Andamento') return 'text-brand-400 bg-brand-500/10';
        return 'text-anthracite-300 bg-anthracite-700/50';
    };

    const manutencoesFiltradas = manutencoes.filter((os) =>
        !searchTerm ||
        os.maquina_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        os.descricao?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <div className="h-full flex flex-col">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 space-y-4 md:space-y-0">
                <div>
                    <h1 className="text-2xl font-bold text-anthracite-50 tracking-tight flex items-center gap-2.5">
                        <PenTool size={22} className="text-red-400" /> Manutenção
                    </h1>
                    <p className="text-[14px] text-anthracite-500 mt-1">Gestão de ativos e ordens de manutenção</p>
                </div>
                <button onClick={handleAbrirNovaOS} className="mes-btn-primary bg-gradient-to-r from-red-600 to-red-500 hover:from-red-500 hover:to-red-400 shadow-[0_4px_16px_rgba(239,68,68,0.3)]"><Plus size={16} /> Nova OS</button>
            </div>

            {/* Listagem */}
            <div className="mes-card flex-1 overflow-hidden flex flex-col">
                <div className="p-5 border-b border-white/[0.04]">
                    <div className="relative max-w-sm">
                        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-anthracite-500" />
                        <input type="text" className="mes-input pl-11" placeholder="Buscar máquina ou OS..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} />
                    </div>
                </div>
                {erro && <div className="px-6 py-3 text-[13px] text-red-400">{erro}</div>}
                <div className="overflow-x-auto">
                    <table className="min-w-full">
                        <thead>
                            <tr className="border-b border-white/[0.04]">
                                <th className="px-6 py-3.5 text-left text-[10px] font-bold text-anthracite-500 uppercase tracking-wider">Equipamento</th>
                                <th className="px-6 py-3.5 text-left text-[10px] font-bold text-anthracite-500 uppercase tracking-wider">Tipo / Descrição</th>
                                <th className="px-6 py-3.5 text-left text-[10px] font-bold text-anthracite-500 uppercase tracking-wider">Status</th>
                                <th className="px-6 py-3.5 text-right text-[10px] font-bold text-anthracite-500 uppercase tracking-wider">Data</th>
                                <th className="px-6 py-3.5 text-right text-[10px] font-bold text-anthracite-500 uppercase tracking-wider">Ações</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-white/[0.03]">
                            {!carregando && manutencoesFiltradas.length === 0 && (
                                <tr><td colSpan={5} className="px-6 py-8 text-center text-[13px] text-anthracite-500">Nenhuma manutenção registrada.</td></tr>
                            )}
                            {manutencoesFiltradas.map(os => (
                                <tr key={os.id} onClick={() => setDetalheOS(os)} className="hover:bg-white/[0.02] transition-colors cursor-pointer">
                                    <td className="px-6 py-4 font-medium text-anthracite-100 text-[13px]">{os.maquina_codigo && os.maquina_nome ? `${os.maquina_codigo} - ${os.maquina_nome}` : (os.maquina_nome || '—')}</td>
                                    <td className="px-6 py-4">
                                        <div className="text-[13px] font-semibold text-anthracite-200">{os.tipo}</div>
                                        <div className="text-[12px] text-anthracite-500 truncate max-w-xs mt-0.5">{os.descricao}</div>
                                    </td>
                                    <td className="px-6 py-4"><span className={`mes-badge ${getStatusColor(os.status)}`}>{os.status}</span></td>
                                    <td className="px-6 py-4 text-right text-[12px] text-anthracite-500">
                                        <div className="flex items-center justify-end gap-1"><Calendar size={14} /> {formatarData(os.data_agendada)}</div>
                                    </td>
                                    <td className="px-6 py-4 text-right">
                                        <div className="flex items-center justify-end gap-2">
                                            <button onClick={(e) => { e.stopPropagation(); setDetalheOS(os); }} title="Ver resumo" className="text-[12px] font-bold text-anthracite-400 hover:text-anthracite-200 p-1.5 rounded-lg bg-white/[0.04] hover:bg-white/[0.08] transition-colors">
                                                <Eye size={14} />
                                            </button>
                                            {os.status !== 'Concluída' && (
                                                <button onClick={(e) => { e.stopPropagation(); handleAbrirConcluirOS(os); }} className="text-[12px] font-bold text-brand-400 hover:text-brand-300 px-3 py-1.5 rounded-lg bg-brand-500/15 hover:bg-brand-500/25 transition-colors">
                                                    Concluir
                                                </button>
                                            )}
                                            <button onClick={(e) => { e.stopPropagation(); setConfirmarExcluir(os); }} disabled={excluindo === os.id} className="text-[12px] font-bold text-red-400 hover:text-red-300 px-3 py-1.5 rounded-lg bg-red-500/15 hover:bg-red-500/25 transition-colors disabled:opacity-50">
                                                {excluindo === os.id ? 'Excluindo...' : 'Excluir'}
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Modal "Nova OS" (25/08/2026) — ver comentário na declaração de
                `novaOS` acima: só a abertura da OS (campos que o formulário
                real preenche na hora do pedido). */}
            {novaOS && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={(e) => { if (e.target === e.currentTarget && !salvando) setNovaOS(null); }}>
                    <div className="mes-modal w-full max-w-lg p-7 animate-slide-up">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-bold text-anthracite-100 flex items-center gap-2">
                                <PenTool size={20} className="text-red-400" /> Nova OS de Manutenção
                            </h2>
                            <button onClick={() => !salvando && setNovaOS(null)} className="p-2 rounded-lg hover:bg-white/[0.04] text-anthracite-500"><X size={18} /></button>
                        </div>

                        <div className="space-y-4">
                            <div>
                                <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Equipamento</label>
                                <select className="mes-input w-full" value={novaOS.maquina_id} onChange={(e) => setNovaOS((f) => ({ ...f, maquina_id: e.target.value }))}>
                                    <option value="">{maquinas.length === 0 ? 'Carregando...' : 'Selecione o equipamento...'}</option>
                                    {maquinas.map((m) => (
                                        <option key={m.id} value={m.id}>{m.codigo_protheus} - {m.nome}</option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Solicitante</label>
                                <input type="text" className="mes-input w-full" value={novaOS.solicitante}
                                    onChange={(e) => setNovaOS((f) => ({ ...f, solicitante: e.target.value }))} placeholder="Nome de quem está solicitando" />
                            </div>

                            <div className="grid grid-cols-3 gap-3">
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Tipo</label>
                                    <select className="mes-input w-full" value={novaOS.tipo} onChange={(e) => setNovaOS((f) => ({ ...f, tipo: e.target.value }))}>
                                        {TIPOS_OS.map((t) => <option key={t} value={t}>{t}</option>)}
                                    </select>
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Data</label>
                                    <input type="date" className="mes-input w-full" value={novaOS.data_agendada}
                                        onChange={(e) => setNovaOS((f) => ({ ...f, data_agendada: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Hora de Parada</label>
                                    <input type="time" className="mes-input w-full" value={novaOS.hora_parada}
                                        onChange={(e) => setNovaOS((f) => ({ ...f, hora_parada: e.target.value }))} />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Descrição do Serviço</label>
                                <textarea className="mes-input w-full resize-none" rows={3} value={novaOS.descricao}
                                    onChange={(e) => setNovaOS((f) => ({ ...f, descricao: e.target.value }))} placeholder="O que precisa ser feito na máquina" />
                            </div>

                            {/* Previsão de término (26/08/2026, pedido do usuário) — não existe no
                                formulário de papel, é um campo de planejamento a mais que o sistema
                                digital pergunta na hora de agendar. A DATA é obrigatória (mesmo dia,
                                pedido do usuário 26/08/2026) — é o que permite ao Gantt do Chão de
                                Fábrica desenhar a janela futura da manutenção sem precisar inventar
                                uma duração; a HORA continua opcional (sem ela, assume fim do dia). */}
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Previsão de Término (data) *</label>
                                    <input type="date" required className="mes-input w-full" value={novaOS.data_prevista}
                                        onChange={(e) => setNovaOS((f) => ({ ...f, data_prevista: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Previsão de Término (hora)</label>
                                    <input type="time" className="mes-input w-full" value={novaOS.hora_prevista}
                                        onChange={(e) => setNovaOS((f) => ({ ...f, hora_prevista: e.target.value }))} />
                                </div>
                            </div>

                            {formErro && <p className="text-red-400 text-[12px]">{formErro}</p>}
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button onClick={() => setNovaOS(null)} disabled={salvando} className="flex-1 mes-btn-ghost border border-white/[0.06] py-3 disabled:opacity-50">Cancelar</button>
                            <button onClick={handleCriarOS} disabled={salvando} className="flex-1 mes-btn-primary bg-red-600 hover:bg-red-500 py-3 disabled:opacity-50">
                                {salvando ? 'Criando...' : 'Criar OS'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal "Concluir OS" (25/08/2026) — ver comentário na declaração
                de `concluirOS` acima: a seção "PARA USO DO EXECUTANTE" do
                formulário real. */}
            {concluirOS && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget && !concluindo) setConcluirOS(null); }}>
                    {/* Modal alargado (26/08/2026, pedido do usuário: "parece muito fino e
                        longo") — max-w-lg com tudo empilhado em 1 coluna virava uma coluna
                        estreita e comprida, com bastante rolagem. max-w-3xl + campos curtos
                        lado a lado (grid) usa a largura em vez de só a altura — 3xl (não 2xl)
                        pra rótulos longos tipo "Responsável pela Análise Final" caberem numa
                        linha só (achado real: em 2xl ainda quebrava em 2 linhas). */}
                    <div className="mes-modal w-full max-w-3xl p-7 my-8 animate-slide-up">
                        <div className="flex items-center justify-between mb-6">
                            <h2 className="text-lg font-bold text-anthracite-100 flex items-center gap-2">
                                <PenTool size={20} className="text-red-400" /> Concluir OS de Manutenção
                            </h2>
                            <button onClick={() => !concluindo && setConcluirOS(null)} className="p-2 rounded-lg hover:bg-white/[0.04] text-anthracite-500"><X size={18} /></button>
                        </div>

                        <div className="space-y-4">
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Defeito</label>
                                    <textarea className="mes-input w-full resize-none" rows={2} value={concluirOS.defeito}
                                        onChange={(e) => setConcluirOS((f) => ({ ...f, defeito: e.target.value }))} placeholder="O que foi identificado" />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Causa</label>
                                    <textarea className="mes-input w-full resize-none" rows={2} value={concluirOS.causa}
                                        onChange={(e) => setConcluirOS((f) => ({ ...f, causa: e.target.value }))} placeholder="Causa raiz identificada" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Ação</label>
                                <textarea className="mes-input w-full resize-none" rows={2} value={concluirOS.acao}
                                    onChange={(e) => setConcluirOS((f) => ({ ...f, acao: e.target.value }))} placeholder="Reparo/ação realizada" />
                            </div>

                            <div className="grid grid-cols-4 gap-3">
                                <div className="col-span-2">
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Recebedor do Serviço Executado</label>
                                    <input type="text" className="mes-input w-full" value={concluirOS.recebedor}
                                        onChange={(e) => setConcluirOS((f) => ({ ...f, recebedor: e.target.value }))} placeholder="Nome de quem recebeu o serviço" />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Data</label>
                                    <input type="date" className="mes-input w-full" value={concluirOS.data_realizada}
                                        onChange={(e) => setConcluirOS((f) => ({ ...f, data_realizada: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Hora</label>
                                    <input type="time" className="mes-input w-full" value={concluirOS.hora_recebedor}
                                        onChange={(e) => setConcluirOS((f) => ({ ...f, hora_recebedor: e.target.value }))} />
                                </div>
                            </div>

                            <div>
                                <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Máquina liberada para produção</label>
                                <div className="flex gap-2">
                                    <button type="button" onClick={() => setConcluirOS((f) => ({ ...f, liberada: true }))}
                                        className={`flex-1 py-2.5 rounded-xl text-[13px] font-semibold border transition-colors ${concluirOS.liberada ? 'bg-petrol-500/15 border-petrol-500/30 text-petrol-400' : 'border-white/[0.06] text-anthracite-500 hover:text-anthracite-300'}`}>
                                        Ok
                                    </button>
                                    <button type="button" onClick={() => setConcluirOS((f) => ({ ...f, liberada: false }))}
                                        className={`flex-1 py-2.5 rounded-xl text-[13px] font-semibold border transition-colors ${!concluirOS.liberada ? 'bg-red-500/15 border-red-500/30 text-red-400' : 'border-white/[0.06] text-anthracite-500 hover:text-anthracite-300'}`}>
                                        Não Ok
                                    </button>
                                </div>
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5 whitespace-nowrap">Responsável pela Análise Final</label>
                                    <input type="text" className="mes-input w-full" value={concluirOS.responsavel_analise}
                                        onChange={(e) => setConcluirOS((f) => ({ ...f, responsavel_analise: e.target.value }))} />
                                </div>
                                <div>
                                    <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5 whitespace-nowrap">Responsável pelo Arquivamento</label>
                                    <input type="text" className="mes-input w-full" value={concluirOS.responsavel_arquivamento}
                                        onChange={(e) => setConcluirOS((f) => ({ ...f, responsavel_arquivamento: e.target.value }))} />
                                </div>
                            </div>
                            <div className="w-1/2 pr-1.5">
                                <label className="block text-[11px] font-bold text-anthracite-400 uppercase tracking-[0.1em] mb-1.5">Data de Arquivamento</label>
                                <input type="date" className="mes-input w-full" value={concluirOS.data_arquivamento}
                                    onChange={(e) => setConcluirOS((f) => ({ ...f, data_arquivamento: e.target.value }))} />
                            </div>

                            {concluirErro && <p className="text-red-400 text-[12px]">{concluirErro}</p>}
                        </div>

                        <div className="flex gap-3 mt-6">
                            <button onClick={() => setConcluirOS(null)} disabled={concluindo} className="flex-1 mes-btn-ghost border border-white/[0.06] py-3 disabled:opacity-50">Cancelar</button>
                            <button onClick={handleConcluirOS} disabled={concluindo} className="flex-1 mes-btn-primary bg-red-600 hover:bg-red-500 py-3 disabled:opacity-50">
                                {concluindo ? 'Salvando...' : 'Concluir OS'}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Modal "Detalhes da OS" (26/08/2026, pedido do usuário) — ver
                comentário na declaração de `detalheOS` acima: resumo
                somente-leitura juntando Abertura + Conclusão. */}
            {detalheOS && (() => {
                const jaPassouPeloFechamento = detalheOS.status !== 'Agendada';
                return (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4 overflow-y-auto" onClick={(e) => { if (e.target === e.currentTarget) setDetalheOS(null); }}>
                        <div className="mes-modal w-full max-w-2xl p-7 my-8 animate-slide-up">
                            <div className="flex items-center justify-between mb-6">
                                <h2 className="text-lg font-bold text-anthracite-100 flex items-center gap-2">
                                    <Eye size={20} className="text-anthracite-400" /> Resumo da OS de Manutenção
                                </h2>
                                <button onClick={() => setDetalheOS(null)} className="p-2 rounded-lg hover:bg-white/[0.04] text-anthracite-500"><X size={18} /></button>
                            </div>

                            <div className="space-y-5">
                                <div className="flex items-center justify-between bg-white/[0.03] p-4 rounded-2xl border border-white/[0.04]">
                                    <div>
                                        <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Equipamento</p>
                                        <p className="text-sm font-semibold text-anthracite-100">{detalheOS.maquina_codigo && detalheOS.maquina_nome ? `${detalheOS.maquina_codigo} - ${detalheOS.maquina_nome}` : (detalheOS.maquina_nome || '—')}</p>
                                    </div>
                                    <span className={`mes-badge ${getStatusColor(detalheOS.status)}`}>{detalheOS.status}</span>
                                </div>

                                <div>
                                    <p className="text-[11px] font-bold text-anthracite-500 uppercase tracking-[0.1em] mb-2.5">Abertura</p>
                                    <div className="grid grid-cols-3 gap-3 mb-3">
                                        <div>
                                            <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Tipo</p>
                                            <p className="text-sm text-anthracite-200">{detalheOS.tipo || '—'}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Data</p>
                                            <p className="text-sm text-anthracite-200">{formatarData(detalheOS.data_agendada)}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Hora de Parada</p>
                                            <p className="text-sm text-anthracite-200">{detalheOS.hora_parada || '—'}</p>
                                        </div>
                                    </div>
                                    <div className="grid grid-cols-3 gap-3 mb-3">
                                        <div>
                                            <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Solicitante</p>
                                            <p className="text-sm text-anthracite-200">{detalheOS.solicitante || '—'}</p>
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Previsão de Término</p>
                                            <p className="text-sm text-anthracite-200">{formatarData(detalheOS.data_prevista)}{detalheOS.hora_prevista ? ` ${detalheOS.hora_prevista}` : ''}</p>
                                        </div>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Descrição do Serviço</p>
                                        <p className="text-sm text-anthracite-200 whitespace-pre-wrap">{detalheOS.descricao || '—'}</p>
                                    </div>
                                </div>

                                {jaPassouPeloFechamento ? (
                                    <div className="pt-4 border-t border-white/[0.06]">
                                        <p className="text-[11px] font-bold text-anthracite-500 uppercase tracking-[0.1em] mb-2.5">Conclusão</p>
                                        <div className="grid grid-cols-3 gap-3 mb-3">
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Recebedor</p>
                                                <p className="text-sm text-anthracite-200">{detalheOS.recebedor || '—'}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Data</p>
                                                <p className="text-sm text-anthracite-200">{formatarData(detalheOS.data_realizada)}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Hora</p>
                                                <p className="text-sm text-anthracite-200">{detalheOS.hora_recebedor || '—'}</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-1 gap-3 mb-3">
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Defeito</p>
                                                <p className="text-sm text-anthracite-200 whitespace-pre-wrap">{detalheOS.defeito || '—'}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Causa</p>
                                                <p className="text-sm text-anthracite-200 whitespace-pre-wrap">{detalheOS.causa || '—'}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Ação</p>
                                                <p className="text-sm text-anthracite-200 whitespace-pre-wrap">{detalheOS.acao || '—'}</p>
                                            </div>
                                        </div>
                                        <div className="grid grid-cols-3 gap-3 items-start">
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Máquina Liberada</p>
                                                <span className={`mes-badge ${detalheOS.liberada === true ? 'text-petrol-400 bg-petrol-500/10' : detalheOS.liberada === false ? 'text-red-400 bg-red-500/10' : 'text-anthracite-300 bg-anthracite-700/50'}`}>
                                                    {detalheOS.liberada === true ? 'Ok' : detalheOS.liberada === false ? 'Não Ok' : '—'}
                                                </span>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Resp. Análise</p>
                                                <p className="text-sm text-anthracite-200">{detalheOS.responsavel_analise || '—'}</p>
                                            </div>
                                            <div>
                                                <p className="text-[10px] font-semibold text-anthracite-500 uppercase tracking-wider mb-1">Resp. Arquivamento</p>
                                                <p className="text-sm text-anthracite-200">{detalheOS.responsavel_arquivamento || '—'}</p>
                                            </div>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="pt-4 border-t border-white/[0.06]">
                                        <p className="text-[13px] text-anthracite-500">Ainda não concluída.</p>
                                    </div>
                                )}
                            </div>

                            <div className="flex gap-3 mt-6">
                                <button onClick={() => setDetalheOS(null)} className="flex-1 mes-btn-ghost border border-white/[0.06] py-3">Fechar</button>
                                {detalheOS.status !== 'Concluída' && (
                                    <button onClick={() => { const os = detalheOS; setDetalheOS(null); handleAbrirConcluirOS(os); }} className="flex-1 mes-btn-primary bg-red-600 hover:bg-red-500 py-3">
                                        Concluir OS
                                    </button>
                                )}
                            </div>
                        </div>
                    </div>
                );
            })()}

            {/* Confirmação de exclusão (26/08/2026) — ver comentário na
                declaração de `confirmarExcluir` acima. */}
            {confirmarExcluir && (
                <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/60 p-4" onClick={(e) => { if (e.target === e.currentTarget) setConfirmarExcluir(null); }}>
                    <div className="mes-modal w-full max-w-sm p-7 animate-slide-up text-center">
                        <div className="w-14 h-14 rounded-2xl bg-red-500/10 flex items-center justify-center mx-auto mb-5">
                            <AlertTriangle size={26} className="text-red-400" />
                        </div>
                        <h2 className="text-lg font-bold text-anthracite-100 mb-2">Excluir OS de Manutenção?</h2>
                        <p className="text-[13px] text-anthracite-500 mb-6">
                            Excluir a OS de manutenção de <span className="font-semibold text-anthracite-300">{confirmarExcluir.maquina_nome || 'equipamento sem nome'}</span>? Essa ação não pode ser desfeita.
                        </p>
                        <div className="flex gap-3">
                            <button onClick={() => setConfirmarExcluir(null)} className="flex-1 mes-btn-ghost border border-white/[0.06] py-3">Cancelar</button>
                            <button onClick={handleExcluirOS} className="flex-1 mes-btn-primary bg-red-600 hover:bg-red-500 py-3">Excluir</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Manutencao;
